/**
 * Featured Collection Premium JavaScript functionality.
 * Manages the persistent wishlist, AJAX cart, and the Quick View modal.
 */

import { Wishlist } from '@theme/wishlist';

console.log("Featured Collection Premium: Script loaded & parsing...");

// Currency Formatter matching Shopify configurations
function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  let value = '';
  let placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  let formatString = format || window.theme?.moneyFormat || '${{amount}}';

  function defaultTo(val, defaultValue) {
    return (val === undefined || val === null) ? defaultValue : val;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ',');
    decimal = defaultTo(decimal, '.');

    if (isNaN(number) || number == null) { return '0'; }

    number = (number / 100.0).toFixed(precision);

    let parts = number.split('.'),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
      centsVal = parts[1] ? (decimal + parts[1]) : '';

    return dollars + centsVal;
  }

  switch (formatString.match(placeholderRegex)?.[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
      break;
  }

  if (value) {
    return formatString.replace(placeholderRegex, value);
  }
  return '$' + (cents / 100).toFixed(2);
}

// Quick View and Variant Picker Modal Manager
class PremiumModalManager {
  constructor() {
    this.modal = document.getElementById('PremiumQuickViewModal');
    if (!this.modal) {
      this.modal = document.createElement('div');
      this.modal.id = 'PremiumQuickViewModal';
      this.modal.className = 'premium-modal';
      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.setAttribute('role', 'dialog');
      this.modal.innerHTML = `
        <div class="premium-modal__overlay"></div>
        <div class="premium-modal__container"></div>
      `;
      document.body.appendChild(this.modal);
    }

    this.overlay = this.modal.querySelector('.premium-modal__overlay');
    this.container = this.modal.querySelector('.premium-modal__container');

    if (this.overlay) this.overlay.addEventListener('click', this.close.bind(this));

    // Handle ESC key close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });
  }

  isOpen() {
    return this.modal && this.modal.classList.contains('is-open');
  }

  open(productHandle, isOnlyVariantPicker = false) {
    if (!this.modal) return;

    // Show loading skeleton inside modal container
    this.modal.classList.add('is-open');
    this.container.innerHTML = `
      <button class="premium-modal__close" aria-label="Close modal">
        <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" fill="none"/></svg>
      </button>
      <div class="premium-modal__content" style="display: flex; align-items: center; justify-content: center; min-height: 250px;">
        <div class="product-card-premium__btn is-loading" style="width: 42px; height: 42px;"></div>
      </div>
    `;

    // Re-bind close trigger
    const newClose = this.container.querySelector('.premium-modal__close');
    if (newClose) newClose.addEventListener('click', this.close.bind(this));

    // Fetch product details via AJAX
    fetch(`/products/${productHandle}.js`)
      .then(res => {
        if (!res.ok) throw new Error('Product not found');
        return res.json();
      })
      .then(product => {
        this.renderProduct(product, isOnlyVariantPicker);
      })
      .catch(err => {
        console.error('Failed to load product for modal:', err);
        this.container.innerHTML = `
          <button class="premium-modal__close" aria-label="Close modal">
            <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" fill="none"/></svg>
          </button>
          <div style="padding: 60px; text-align: center; color: #ff3f40; font-weight: 600;">
            Failed to load product details.
          </div>
        `;
        const newClose = this.container.querySelector('.premium-modal__close');
        if (newClose) newClose.addEventListener('click', this.close.bind(this));
      });
  }

  close() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
  }

  openWishlist() {
    if (!this.modal) return;

    this.modal.classList.add('is-open');
    this.container.innerHTML = `
      <button class="premium-modal__close" aria-label="Close modal">
        <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" fill="none"/></svg>
      </button>
      <div class="premium-modal__content" style="grid-template-columns: 1fr; padding: 40px 24px;">
        <h2 class="premium-modal__title" style="text-align: center; margin-bottom: 24px; font-size: 24px; font-weight: 700;">My Wishlist</h2>
        <div class="wishlist-modal-grid" data-wishlist-modal-grid style="display: flex; flex-direction: column; gap: 16px;">
          <div class="product-card-premium__btn is-loading" style="width: 40px; height: 40px; margin: 40px auto;"></div>
        </div>
      </div>
    `;

    // Re-bind close trigger
    const closeBtn = this.container.querySelector('.premium-modal__close');
    if (closeBtn) closeBtn.addEventListener('click', this.close.bind(this));

    const renderWishlistItems = () => {
      const grid = this.container.querySelector('[data-wishlist-modal-grid]');
      if (!grid) return;

      const wishlistIds = Wishlist.getItems();
      if (wishlistIds.length === 0) {
        grid.innerHTML = `<p style="text-align: center; color: #666666; margin: 40px 0; font-size: 14px;">Your wishlist is empty.</p>`;
        return;
      }

      // Fetch details for all wishlisted items
      let map = {};
      try {
        map = JSON.parse(localStorage.getItem('wishlist-id-map') || '{}');
      } catch (e) {
        console.error(e);
      }

      const fetchPromises = wishlistIds.map(id => {
        const handle = map[id];
        if (handle) {
          return fetch(`/products/${handle}.js`)
            .then(res => {
              if (!res.ok) throw new Error('Product not found');
              return res.json();
            })
            .catch(() => null);
        } else {
          // Fallback search suggest to get the product handle from its ID
          return fetch(`/search/suggest.json?q=id:${id}&resources[type]=product`)
            .then(res => res.json())
            .then(data => {
              const productSuggestion = data.resources?.results?.products?.[0];
              if (productSuggestion && productSuggestion.handle) {
                // Update map for next time
                try {
                  const currentMap = JSON.parse(localStorage.getItem('wishlist-id-map') || '{}');
                  currentMap[id] = productSuggestion.handle;
                  localStorage.setItem('wishlist-id-map', JSON.stringify(currentMap));
                } catch (e) { }

                return fetch(`/products/${productSuggestion.handle}.js`).then(res => res.json()).catch(() => null);
              }
              return null;
            })
            .catch(() => null);
        }
      });

      Promise.all(fetchPromises).then(products => {
        const validProducts = products.filter(p => p !== null);

        if (validProducts.length === 0) {
          grid.innerHTML = `<p style="text-align: center; color: #666666; margin: 40px 0; font-size: 14px;">Your wishlist is empty.</p>`;
          return;
        }

        grid.innerHTML = '';

        validProducts.forEach(product => {
          const itemDiv = document.createElement('div');
          itemDiv.className = 'wishlist-modal-item';
          itemDiv.style.cssText = 'display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 12px; border: 1px solid #f0f0f0; border-radius: 16px; background-color: #FFF8F5;';

          const leftDiv = document.createElement('div');
          leftDiv.style.cssText = 'display: flex; align-items: center; gap: 12px; flex-grow: 1; min-width: 0;';

          const img = document.createElement('img');
          img.src = product.featured_image || '';
          img.alt = product.title;
          img.style.cssText = 'width: 60px; height: 60px; object-fit: contain; background: #ffffff; border-radius: 12px; flex-shrink: 0;';
          leftDiv.appendChild(img);

          const titlePriceDiv = document.createElement('div');
          titlePriceDiv.style.cssText = 'display: flex; flex-direction: column; min-width: 0;';

          const titleLink = document.createElement('a');
          titleLink.href = product.url;
          titleLink.textContent = product.title;
          titleLink.style.cssText = 'font-size: 14px; font-weight: 700; color: #111111; text-decoration: none; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
          titlePriceDiv.appendChild(titleLink);

          const priceSpan = document.createElement('span');
          priceSpan.textContent = formatMoney(product.price);
          priceSpan.style.cssText = 'font-size: 14px; font-weight: 600; color: #666666; margin-top: 4px;';
          titlePriceDiv.appendChild(priceSpan);

          leftDiv.appendChild(titlePriceDiv);
          itemDiv.appendChild(leftDiv);

          const rightDiv = document.createElement('div');
          rightDiv.style.cssText = 'display: flex; align-items: center; gap: 8px; flex-shrink: 0;';

          // Add to Cart Button (redirects to product picker if multi-variant, adds directly if single)
          const atcBtn = document.createElement('button');
          atcBtn.className = 'product-card-premium__btn product-card-premium__btn--primary';
          atcBtn.style.cssText = 'height: 40px; font-size: 12px; padding: 0 16px; border-radius: 99px;';

          const hasVariants = product.variants.length > 1;
          atcBtn.textContent = hasVariants ? 'Select Options' : (product.available ? 'Add' : 'Sold Out');
          if (!product.available && !hasVariants) atcBtn.disabled = true;

          atcBtn.addEventListener('click', () => {
            if (hasVariants) {
              this.close();
              this.open(product.handle, true);
            } else {
              atcBtn.classList.add('is-loading');
              atcBtn.disabled = true;

              // Collect section IDs to update drawer HTML dynamically
              const cartItems = document.querySelectorAll('cart-items-component');
              const sectionsToUpdate = [];
              cartItems.forEach((item) => {
                if (item instanceof HTMLElement && item.dataset.sectionId) {
                  sectionsToUpdate.push(item.dataset.sectionId);
                }
              });

              const requestBody = {
                id: product.variants[0].id,
                quantity: 1
              };

              if (sectionsToUpdate.length > 0) {
                requestBody.sections = sectionsToUpdate.join(',');
              }

              fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                },
                body: JSON.stringify(requestBody)
              })
                .then(res => res.json())
                .then(added => {
                  if (added.status || added.errors) throw new Error(added.description || added.message || 'Cart error');
                  atcBtn.classList.remove('is-loading');
                  atcBtn.classList.add('is-success');
                  atcBtn.textContent = 'Added';

                  fetch('/cart.js')
                    .then(res => res.json())
                    .then(cart => {
                      document.dispatchEvent(new CustomEvent('cart:update', {
                        bubbles: true,
                        detail: {
                          resource: cart,
                          sourceId: 'featured-collection-premium',
                          data: {
                            itemCount: cart.item_count,
                            source: 'featured-collection-premium',
                            sections: added.sections || {}
                          }
                        }
                      }));

                      const drawer = document.querySelector('cart-drawer-component');
                      if (drawer && typeof drawer.open === 'function') {
                        drawer.open();
                      }

                      setTimeout(() => {
                        this.close();
                      }, 800);
                    });
                })
                .catch(err => {
                  console.error(err);
                  atcBtn.classList.remove('is-loading');
                  atcBtn.classList.add('is-error');
                  atcBtn.textContent = 'Error';
                  setTimeout(() => {
                    atcBtn.classList.remove('is-error');
                    atcBtn.disabled = false;
                    atcBtn.textContent = 'Add';
                  }, 2000);
                });
            }
          });
          rightDiv.appendChild(atcBtn);

          // Remove Button
          const removeBtn = document.createElement('button');
          removeBtn.className = 'product-card-premium__btn product-card-premium__btn--secondary';
          removeBtn.style.cssText = 'width: 40px; height: 40px; padding: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center;';
          removeBtn.innerHTML = `<svg viewBox="0 0 24 24" style="width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 2.2;"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>`;
          removeBtn.addEventListener('click', () => {
            Wishlist.toggle(product.id);
            renderWishlistItems(); // Re-render wishlist inside modal
          });
          rightDiv.appendChild(removeBtn);

          itemDiv.appendChild(rightDiv);
          grid.appendChild(itemDiv);
        });
      });
    };

    renderWishlistItems();
  }

  renderProduct(product, isOnlyVariantPicker) {
    // Determine initial active variant (first available, or fallback to first option)
    let currentVariant = product.variants.find(v => v.available) || product.variants[0];
    let selectedOptions = [...currentVariant.options];

    // Build markup
    this.container.innerHTML = `
      <button class="premium-modal__close" aria-label="Close modal">
        <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" fill="none"/></svg>
      </button>
      <div class="premium-modal__content${isOnlyVariantPicker ? ' premium-modal__content--variant-only' : ''}">
        ${!isOnlyVariantPicker ? `
          <div class="premium-modal__gallery">
            <img src="${currentVariant.featured_image?.src || product.featured_image || ''}" alt="${product.title}" data-modal-image>
          </div>
        ` : ''}
        <div class="premium-modal__info">
          <h2 class="premium-modal__title">${product.title}</h2>
          <div class="premium-modal__price-wrapper">
            <span class="premium-modal__price" data-modal-price></span>
            <span class="premium-modal__compare-price" data-modal-compare-price></span>
          </div>
          
          <div class="premium-modal__options-container" data-options-wrapper></div>
          
          <div class="premium-modal__actions-block">
            <div class="premium-modal__quantity-and-add">
              <div class="premium-modal__quantity">
                <button class="premium-modal__quantity-btn" data-qty-minus type="button">-</button>
                <input class="premium-modal__quantity-input" type="number" value="1" min="1" data-qty-input aria-label="Quantity">
                <button class="premium-modal__quantity-btn" data-qty-plus type="button">+</button>
              </div>
              <button class="product-card-premium__btn product-card-premium__btn--primary premium-modal__add-btn" data-modal-atc type="button">
                Add to Cart
              </button>
            </div>
            
            <a class="premium-modal__view-details" href="${product.url}">View full details</a>
            <div class="premium-modal__error-message" data-modal-error></div>
          </div>
        </div>
      </div>
    `;

    // Re-bind close triggers
    const closeBtn = this.container.querySelector('.premium-modal__close');
    if (closeBtn) closeBtn.addEventListener('click', this.close.bind(this));

    // Element references
    const imgEl = this.container.querySelector('[data-modal-image]');
    const priceEl = this.container.querySelector('[data-modal-price]');
    const comparePriceEl = this.container.querySelector('[data-modal-compare-price]');
    const optionsWrapper = this.container.querySelector('[data-options-wrapper]');
    const qtyInput = this.container.querySelector('[data-qty-input]');
    const qtyMinus = this.container.querySelector('[data-qty-minus]');
    const qtyPlus = this.container.querySelector('[data-qty-plus]');
    const atcEl = this.container.querySelector('[data-modal-atc]');
    const errorEl = this.container.querySelector('[data-modal-error]');

    // Find a variant that matches currently selected options array
    const findVariant = () => {
      return product.variants.find(variant => {
        return variant.options.every((opt, index) => opt === selectedOptions[index]);
      });
    };

    // Update modal elements based on selected variant state
    const updateModalDOM = (variant) => {
      if (errorEl) errorEl.style.display = 'none';

      if (variant) {
        // Image update
        if (variant.featured_image && variant.featured_image.src && imgEl) {
          imgEl.src = variant.featured_image.src;
        } else if (product.featured_image && imgEl) {
          imgEl.src = product.featured_image;
        }

        // Price update
        if (priceEl) priceEl.textContent = formatMoney(variant.price);

        // Compare price update
        if (comparePriceEl) {
          if (variant.compare_at_price > variant.price) {
            comparePriceEl.textContent = formatMoney(variant.compare_at_price);
            comparePriceEl.style.display = 'inline';
          } else {
            comparePriceEl.style.display = 'none';
          }
        }

        // Button state update
        if (atcEl) {
          if (variant.available) {
            atcEl.disabled = false;
            atcEl.textContent = 'Add to Cart';
          } else {
            atcEl.disabled = true;
            atcEl.textContent = 'Sold Out';
          }
        }
      } else {
        // Variant does not exist / unavailable
        if (priceEl) priceEl.textContent = '';
        if (comparePriceEl) comparePriceEl.style.display = 'none';
        if (atcEl) {
          atcEl.disabled = true;
          atcEl.textContent = 'Unavailable';
        }
      }
    };

    // Render Option selectors (swatches or buttons)
    if (optionsWrapper && product.options) {
      optionsWrapper.innerHTML = '';

      product.options.forEach((option, optionIndex) => {
        // Skip default variant picker elements if product has only 1 variant (Title: Default Title)
        if (option.name === 'Title' && option.values.includes('Default Title')) return;

        const optionDiv = document.createElement('div');
        optionDiv.className = 'premium-modal__option';

        const optionLabel = document.createElement('span');
        optionLabel.className = 'premium-modal__option-name';
        optionLabel.textContent = option.name;
        optionDiv.appendChild(optionLabel);

        const valuesContainer = document.createElement('div');
        valuesContainer.className = 'premium-modal__option-values';

        option.values.forEach(value => {
          const isColor = option.name.toLowerCase() === 'color' || option.name.toLowerCase() === 'colour';
          const btn = document.createElement('button');
          btn.className = 'premium-modal__value-btn';
          btn.type = 'button';
          btn.setAttribute('aria-label', `${option.name}: ${value}`);

          if (isColor) {
            btn.classList.add('premium-modal__value-btn--color');
            const cleanVal = value.toLowerCase().replace(/\s/g, '');
            btn.style.backgroundColor = cleanVal;
          } else {
            btn.textContent = value;
          }

          // Active selection highlight
          if (selectedOptions[optionIndex] === value) {
            btn.classList.add('is-selected');
          }

          // Bind option selection
          btn.addEventListener('click', () => {
            valuesContainer.querySelectorAll('.premium-modal__value-btn').forEach(b => b.classList.remove('is-selected'));
            btn.classList.add('is-selected');

            selectedOptions[optionIndex] = value;
            const matched = findVariant();
            updateModalDOM(matched);
          });

          valuesContainer.appendChild(btn);
        });

        optionDiv.appendChild(valuesContainer);
        optionsWrapper.appendChild(optionDiv);
      });
    }

    // Set initial pricing states
    updateModalDOM(currentVariant);

    // Quantity selectors interaction
    if (qtyMinus && qtyInput) {
      qtyMinus.addEventListener('click', () => {
        let val = parseInt(qtyInput.value, 10) || 1;
        if (val > 1) qtyInput.value = val - 1;
      });
    }

    if (qtyPlus && qtyInput) {
      qtyPlus.addEventListener('click', () => {
        let val = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = val + 1;
      });
    }

    // AJAX Form submit in modal
    if (atcEl) {
      atcEl.addEventListener('click', () => {
        const matched = findVariant();
        if (!matched) return;

        const qty = parseInt(qtyInput.value, 10) || 1;

        atcEl.classList.add('is-loading');
        atcEl.disabled = true;

        // Query any section ID dynamically inside cart drawer to keep drawer HTML updated
        const cartItems = document.querySelectorAll('cart-items-component');
        const sectionsToUpdate = [];
        cartItems.forEach((item) => {
          if (item instanceof HTMLElement && item.dataset.sectionId) {
            sectionsToUpdate.push(item.dataset.sectionId);
          }
        });

        const requestBody = {
          id: matched.id,
          quantity: qty
        };

        if (sectionsToUpdate.length > 0) {
          requestBody.sections = sectionsToUpdate.join(',');
        }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(requestBody)
        })
          .then(res => res.json())
          .then(added => {
            if (added.status || added.errors) {
              throw new Error(added.description || added.message || 'Cart operation failed.');
            }

            atcEl.classList.remove('is-loading');
            atcEl.classList.add('is-success');
            atcEl.textContent = 'Added!';

            // Fetch final cart state
            fetch('/cart.js')
              .then(res => res.json())
              .then(cart => {
                // Dispatch event matching theme events
                document.dispatchEvent(new CustomEvent('cart:update', {
                  bubbles: true,
                  detail: {
                    resource: cart,
                    sourceId: 'featured-collection-premium',
                    data: {
                      itemCount: cart.item_count,
                      source: 'featured-collection-premium',
                      sections: added.sections || {}
                    }
                  }
                }));

                // Auto-open theme cart drawer
                const drawer = document.querySelector('cart-drawer-component');
                if (drawer && typeof drawer.open === 'function') {
                  drawer.open();
                }

                setTimeout(() => {
                  this.close();
                }, 800);
              });
          })
          .catch(err => {
            console.error('AJAX add to cart failed:', err);
            atcEl.classList.remove('is-loading');
            atcEl.classList.add('is-error');
            atcEl.textContent = 'Error!';

            if (errorEl) {
              errorEl.textContent = err.message || 'Adding failed. Please try again.';
              errorEl.style.display = 'block';
            }

            setTimeout(() => {
              atcEl.classList.remove('is-error');
              atcEl.disabled = false;
              atcEl.textContent = 'Add to Cart';
            }, 2000);
          });
      });
    }
  }
}

// Product Card Custom Element
class ProductCardPremium extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle;
    this.productTitle = this.dataset.productTitle;

    console.log("Featured Collection Premium: Connected callback fired for:", this.productHandle);

    // Save mapping of ID to Handle so that the wishlist modal can resolve handles from product IDs
    if (this.productId && this.productHandle) {
      try {
        const map = JSON.parse(localStorage.getItem('wishlist-id-map') || '{}');
        map[this.productId] = this.productHandle;
        localStorage.setItem('wishlist-id-map', JSON.stringify(map));
      } catch (e) {
        console.error('Failed to update wishlist handle map:', e);
      }
    }

    this.atcForm = this.querySelector('[data-atc-form]');
    this.quickViewBtn = this.querySelector('[data-quick-view-trigger]');
    this.variantPickerBtn = this.querySelector('[data-variant-picker-trigger]');

    // Bindings
    if (this.atcForm) {
      this.atcForm.addEventListener('submit', this.handleFormAddToCart.bind(this));
    }
    if (this.quickViewBtn) {
      this.quickViewBtn.addEventListener('click', this.handleQuickViewClick.bind(this));
    }
    if (this.variantPickerBtn) {
      this.variantPickerBtn.addEventListener('click', this.handleVariantPickerClick.bind(this));
    }
  }

  handleFormAddToCart(e) {
    e.preventDefault();

    console.log("Featured Collection Premium: Add to cart form submitted for product:", this.productHandle);

    const form = this.atcForm;
    const submitBtn = form.querySelector('[type="submit"]');
    if (!submitBtn) return;

    const variantId = form.querySelector('[name="id"]').value;
    const quantity = form.querySelector('[name="quantity"]')?.value || 1;

    submitBtn.classList.add('is-loading');
    submitBtn.disabled = true;

    // Collect section IDs to update drawer HTML dynamically
    const cartItems = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = [];
    cartItems.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.push(item.dataset.sectionId);
      }
    });

    const requestBody = {
      id: parseInt(variantId, 10),
      quantity: parseInt(quantity, 10)
    };

    if (sectionsToUpdate.length > 0) {
      requestBody.sections = sectionsToUpdate.join(',');
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestBody)
    })
      .then(res => res.json())
      .then(added => {
        if (added.status || added.errors) {
          throw new Error(added.description || added.message || 'Cart operation failed.');
        }

        submitBtn.classList.remove('is-loading');
        submitBtn.classList.add('is-success');
        const originalText = submitBtn.textContent;
        submitBtn.textContent = 'Added!';

        // Fetch final cart count to trigger header/drawer updates
        fetch('/cart.js')
          .then(res => res.json())
          .then(cart => {
            // Dispatch standard theme event
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: 'featured-collection-premium',
                data: {
                  itemCount: cart.item_count,
                  source: 'featured-collection-premium',
                  sections: added.sections || {}
                }
              }
            }));

            // Auto-open theme drawer
            const drawer = document.querySelector('cart-drawer-component');
            if (drawer && typeof drawer.open === 'function') {
              drawer.open();
            }

            setTimeout(() => {
              submitBtn.classList.remove('is-success');
              submitBtn.disabled = false;
              submitBtn.textContent = originalText;
            }, 2000);
          });
      })
      .catch(err => {
        console.error('Featured Collection Premium: Card AJAX form add failed:', err);
        submitBtn.classList.remove('is-loading');
        submitBtn.classList.add('is-error');
        const originalText = submitBtn.textContent;
        submitBtn.textContent = 'Error!';

        setTimeout(() => {
          submitBtn.classList.remove('is-error');
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
        }, 2000);
      });
  }

  handleQuickViewClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (window.premiumModalManager) {
      window.premiumModalManager.open(this.productHandle, false);
    }
  }

  handleVariantPickerClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (window.premiumModalManager) {
      window.premiumModalManager.open(this.productHandle, true);
    }
  }
}

// Initialize Custom Elements and Singleton Modal Manager immediately
console.log("Featured Collection Premium: Attempting custom element definition...");
if (!customElements.get('product-card-premium')) {
  customElements.define('product-card-premium', ProductCardPremium);
  console.log("Featured Collection Premium: Custom element successfully registered!");
}

if (!window.premiumModalManager) {
  window.premiumModalManager = new PremiumModalManager();
  console.log("Featured Collection Premium: Singleton Modal Manager instantiated.");
}

// Re-initialize for Shopify Theme Editor sections updates
document.addEventListener('shopify:section:load', () => {
  if (!window.premiumModalManager) {
    window.premiumModalManager = new PremiumModalManager();
  } else {
    // Refresh modal reference in case section DOM replaced it
    window.premiumModalManager.modal = document.getElementById('PremiumQuickViewModal');
    if (window.premiumModalManager.modal) {
      window.premiumModalManager.overlay = window.premiumModalManager.modal.querySelector('.premium-modal__overlay');
      window.premiumModalManager.closeBtn = window.premiumModalManager.modal.querySelector('.premium-modal__close');
      window.premiumModalManager.container = window.premiumModalManager.modal.querySelector('.premium-modal__container');

      if (window.premiumModalManager.overlay) {
        window.premiumModalManager.overlay.addEventListener('click', window.premiumModalManager.close.bind(window.premiumModalManager));
      }
      if (window.premiumModalManager.closeBtn) {
        window.premiumModalManager.closeBtn.addEventListener('click', window.premiumModalManager.close.bind(window.premiumModalManager));
      }
    }
  }
  updateHeaderWishlistCount();
  initHeaderWishlistButtons();
});

// Wishlist Header Sync Utilities
function updateHeaderWishlistCount() {
  const bubbles = document.querySelectorAll('[data-wishlist-count-bubble]');
  if (bubbles.length === 0) return;

  const count = Wishlist.getCount();

  bubbles.forEach(bubble => {
    bubble.textContent = count;
    // Appearance lives in CSS (header-actions.liquid); only toggle visibility here.
    bubble.style.display = count > 0 ? 'flex' : 'none';
  });
}

function initHeaderWishlistButtons() {
  const headerBtns = document.querySelectorAll('[data-wishlist-header-button]');
  headerBtns.forEach(btn => {
    if (btn.dataset.wishlistBound === 'true') return;
    btn.dataset.wishlistBound = 'true';

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (window.premiumModalManager) {
        window.premiumModalManager.openWishlist();
      }
    });
  });
}

// Bind load interactions
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    updateHeaderWishlistCount();
    initHeaderWishlistButtons();
  });
} else {
  updateHeaderWishlistCount();
  initHeaderWishlistButtons();
}

document.addEventListener('wishlist:update', updateHeaderWishlistCount);
